import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, ArrowLeft, Plus, Minus, AlertCircle, X, Phone } from 'lucide-react';
import { toast } from 'sonner';

import { NavbarPublic } from '../../components/NavbarPublic.jsx';
import { api } from '@/lib/api.ts';
import { formatCurrency } from '../../lib/format.ts';
import { useCarrito } from '../../features/carrito/useCarrito.ts';
import { usePublicStorePricingRate, formatDualPrice } from '../../lib/dolar.ts';

// --- COMPONENTE DE FONDO AMBIENTAL ---
const AmbientBackground = () => (
  <div className="fixed inset-0 -z-10 bg-[#050505] overflow-hidden pointer-events-none">
    <div className="absolute top-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-indigo-900/20 rounded-full blur-[120px] opacity-50 mix-blend-screen" />
    <div className="absolute bottom-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-amber-900/10 rounded-full blur-[150px] opacity-40 mix-blend-screen" />
  </div>
);

export function DetalleProducto() {
  const { slug, productoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const carrito = useCarrito();
  const { data: pricingRate } = usePublicStorePricingRate(slug);

  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // ✅ Detectar si venimos del marketplace
  const fromMarketplace = location.state?.fromProduct;

  // Fetch producto
  const { data: producto, isLoading } = useQuery({
    queryKey: ['producto', productoId],
    queryFn: async () => {
      const { data } = await api.get(`/api/stores/${slug}/products/${productoId}`);
      return data;
    },
    enabled: Boolean(productoId),
    // ✅ STOCK REAL-TIME: Actualización automática para stock
    staleTime: 0,             // Datos siempre considerados obsoletos
    refetchInterval: 30000,   // Actualizar cada 30 segundos
    refetchOnWindowFocus: true, // Actualizar al enfocar ventana
    refetchOnMount: true,     // Actualizar al montar componente
  });

  // Fetch datos de la tienda para WhatsApp
  const { data: storeInfo } = useQuery({
    queryKey: ['tienda', slug],
    queryFn: async () => {
      const { data } = await api.get(`/api/stores/${slug}/public`);
      return data;
    },
    enabled: Boolean(slug),
  });

  // Extraer tipos de atributos únicos
  const attributeTypes = useMemo(() => {
    if (!producto?.variants || producto.variants.length === 0) return {};
    const types = {};
    producto.variants.forEach(variant => {
      const attrs = variant.attributes || {};
      Object.keys(attrs).forEach(key => {
        if (!types[key]) types[key] = new Set();
        types[key].add(attrs[key]);
      });
    });
    Object.keys(types).forEach(key => {
      types[key] = Array.from(types[key]);
    });
    return types;
  }, [producto]);

  // Filtrar opciones disponibles según selección actual
  const availableOptions = useMemo(() => {
    if (!producto?.variants || producto.variants.length === 0) return {};
    
    const available = {};
    const attributeKeys = Object.keys(attributeTypes);
    
    attributeKeys.forEach(currentKey => {
      available[currentKey] = new Set();
      
      // Filtrar variantes que coincidan con las selecciones actuales (excepto la actual)
      const matchingVariants = producto.variants.filter(variant => {
        const attrs = variant.attributes || {};
        return attributeKeys.every(key => {
          if (key === currentKey) return true; // No filtrar por el atributo actual
          if (!selectedAttributes[key]) return true; // Si no está seleccionado, no filtrar
          const variantValue = (attrs[key] || '').toString().toLowerCase();
          const selectedValue = (selectedAttributes[key] || '').toString().toLowerCase();
          return variantValue === selectedValue;
        });
      });
      
      // Agregar opciones disponibles para este atributo
      matchingVariants.forEach(variant => {
        const attrs = variant.attributes || {};
        if (attrs[currentKey]) {
          available[currentKey].add(attrs[currentKey]);
        }
      });
      
      available[currentKey] = Array.from(available[currentKey]);
    });
    
    return available;
  }, [producto, selectedAttributes, attributeTypes]);

  // Recolectar todas las imágenes (principal + galería + variantes)
  const allImages = useMemo(() => {
    const images = [];
    
    // 1. Imagen principal
    if (producto?.image) images.push(producto.image);
    
    // 2. Galería adicional (media)
    if (producto?.media && Array.isArray(producto.media)) {
      producto.media.forEach(mediaItem => {
        if (mediaItem.file && !images.includes(mediaItem.file)) {
          images.push(mediaItem.file);
        }
      });
    }
    
    // 3. Imágenes de variantes
    if (producto?.variants) {
      producto.variants.forEach(variant => {
        if (variant.image && !images.includes(variant.image)) {
          images.push(variant.image);
        }
      });
    }
    
    return images.length > 0 ? images : ['https://placehold.co/600x600/1a1a1a/ffffff?text=Producto'];
  }, [producto]);

  // Encontrar variante seleccionada
  const selectedVariant = useMemo(() => {
    if (!producto?.variants || producto.variants.length === 0) return null;
    const attributeTypesKeys = Object.keys(attributeTypes);
    const allSelected = attributeTypesKeys.every(key => selectedAttributes[key]);
    if (!allSelected) return null;
    return producto.variants.find(variant => {
      const attrs = variant.attributes || {};
      return attributeTypesKeys.every(key => {
        const variantValue = (attrs[key] || '').toString().toLowerCase();
        const selectedValue = (selectedAttributes[key] || '').toString().toLowerCase();
        return variantValue === selectedValue;
      });
    });
  }, [producto, selectedAttributes, attributeTypes]);

  const hasVariants = producto?.variants && producto.variants.length > 0;
  const displayImage = selectedVariant?.image || allImages[selectedImageIndex] || producto?.image;
  
  const originalPrice = selectedVariant?.price || producto?.price || 0;
  const discountedPrice = selectedVariant ? (selectedVariant.discounted_price || originalPrice) : (producto?.discounted_price || originalPrice);
  const displayPrice = discountedPrice;
  const hasDiscount = discountedPrice < originalPrice;
  const discountPercentage = selectedVariant ? (selectedVariant.discount_percentage || 0) : (producto?.discount_percentage || 0);
  
  const displayStock = useMemo(() => {
    if (selectedVariant) return selectedVariant.stock || 0;
    if (hasVariants && producto?.variants) return producto.variants.reduce((total, variant) => total + (variant.stock || 0), 0);
    return producto?.inventory || 0;
  }, [selectedVariant, hasVariants, producto]);

  const handleAttributeSelect = (type, value) => {
    setSelectedAttributes(prev => {
      if (prev[type] === value) {
        const newAttributes = { ...prev };
        delete newAttributes[type];
        return newAttributes;
      }
      return { ...prev, [type]: value };
    });
  };

  // LÓGICA DE CAMBIO DE CANTIDAD
  const handleQuantityChange = (e) => {
    if (e.target.value === '') {
        setQuantity(''); 
        return;
    }
    let val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    if (val > displayStock) {
        val = displayStock;
        toast.warning(`Solo hay ${displayStock} unidades disponibles`);
    }
    setQuantity(val);
  };

  const handleBlur = () => {
     if (quantity === '' || quantity < 1) setQuantity(1);
  };

  // LÓGICA DE CARRITO (Actualizada con sync)
  const handleAddToCart = () => {
    if (hasVariants && !selectedVariant) {
      toast.error('Por favor selecciona una opción de cada categoría');
      return;
    }
    if (displayStock === 0) {
      toast.error('Producto agotado');
      return;
    }
    const finalQty = quantity === '' ? 1 : Number(quantity);
    const item = {
      id: selectedVariant ? `${producto.id}-${selectedVariant.id}` : String(producto.id),
      name: selectedVariant ? `${producto.name} - ${selectedVariant.name}` : producto.name,
      price: displayPrice,
      quantity: finalQty,
      image: displayImage,
      variant_attributes: selectedVariant ? selectedVariant.attributes : null,
    };
    
    // IMPORTANTE: Pasamos 'slug' como segundo argumento para la sincronización en tiempo real
    carrito.addItem(item, slug);
    
    // Toast opcional si quieres feedback visual además de abrir el carrito
    toast.success('Producto añadido'); 
  };

  const allAttributesSelected = Object.keys(attributeTypes).every(type => selectedAttributes[type]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!producto) return null;

  return (
    <div className="min-h-screen text-white font-sans selection:bg-indigo-500/30 relative z-0">
      <AmbientBackground />
      <NavbarPublic />
      
      <main className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 pt-28 pb-12 sm:pt-32">
        
        {/* BOTÓN VOLVER MEJORADO */}
        <div className="mb-8">
          <button
            onClick={() => {
              if (fromMarketplace) {
                // Volver al marketplace con scroll restoration
                navigate('/marketplace', { state: { fromProduct: true } });
              } else {
                // Volver al catálogo de la tienda
                navigate(`/t/${slug}`);
              }
            }}
            className="group flex items-center gap-2 text-sm font-medium text-white/50 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            <span className="uppercase tracking-widest text-xs font-bold">
              {fromMarketplace ? 'Volver al marketplace' : 'Volver al catálogo'}
            </span>
          </button>
        </div>

        <div className="grid gap-12 lg:grid-cols-2 items-start">
          
          {/* --- GALERÍA --- */}
          <div className="space-y-6">
            <div 
              className="relative aspect-square w-full rounded-[40px] bg-[#0B0B0B] border border-white/5 overflow-hidden flex items-center justify-center group cursor-zoom-in"
              onClick={() => setIsImageModalOpen(true)}
            >
               <div className="absolute inset-0 bg-gradient-to-b from-[#151515] to-[#0B0B0B]" />
               <div className="absolute w-2/3 h-2/3 bg-white/5 rounded-full blur-[60px] group-hover:bg-white/10 transition-all duration-700" />
               
               <img
                  src={allImages[selectedImageIndex]}
                  alt={producto.name}
                  className="relative z-10 w-full h-full object-contain p-8 sm:p-12 transition-transform duration-500 group-hover:scale-110"
                  style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.6))' }}
                  onError={(e) => { e.target.src = 'https://placehold.co/600x600/1a1a1a/ffffff?text=Producto'; }}
               />

               <div className="absolute top-6 left-6 z-20 flex flex-col gap-2">
                  {hasDiscount && (
                     <span className="px-3 py-1.5 rounded-full bg-indigo-500/20 border border-indigo-500/50 text-indigo-200 text-xs font-bold tracking-widest uppercase backdrop-blur-md">
                        -{discountPercentage}% OFF
                     </span>
                  )}
                  {displayStock > 0 && displayStock < 5 && (
                     <span className="px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-200 text-xs font-bold tracking-widest uppercase backdrop-blur-md">
                        Últimas {displayStock}
                     </span>
                  )}
               </div>
            </div>

            {allImages.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`relative aspect-square rounded-2xl overflow-hidden border transition-all duration-300 ${
                      selectedImageIndex === index
                        ? 'border-indigo-500 ring-2 ring-indigo-500/20 bg-[#1a1a1a]'
                        : 'border-white/10 bg-[#0B0B0B] hover:border-white/30'
                    }`}
                  >
                    <img src={image} alt="" className="w-full h-full object-contain p-2" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* --- INFO --- */}
          <div className="flex flex-col gap-8 lg:pt-4">
             
             <div>
                <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4 leading-tight tracking-tight">
                   {producto.name}
                </h1>
                <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-6 font-light border-l-2 border-indigo-500/50 pl-4">
                   {producto.description}
                </p>

                <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                   <div>
                      <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-1">Precio Actual</p>
                      <div className="flex items-baseline gap-3">
                         <span className="text-4xl font-light text-white tracking-tight">
                            {formatCurrency(displayPrice)}
                         </span>
                         {hasDiscount && (
                            <span className="text-lg text-white/30 line-through">
                               {formatCurrency(originalPrice)}
                            </span>
                         )}
                      </div>
                   </div>
                   
                   {pricingRate && (
                      <div className="text-right sm:text-left">
                         <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-1">En Moneda Local</p>
                         <p className="text-xl font-medium text-indigo-300">
                            {formatDualPrice(displayPrice, pricingRate).local}
                         </p>
                      </div>
                   )}
                </div>
             </div>

             {/* Variantes */}
             {hasVariants && (
                <div className="space-y-6">
                   {Object.entries(attributeTypes).map(([type, values]) => (
                      <div key={type}>
                         <div className="flex justify-between mb-3">
                            <span className="text-sm font-bold text-white uppercase tracking-wider">{type}</span>
                            <span className="text-xs text-white/40 font-mono">
                               {selectedAttributes[type] ? selectedAttributes[type] : 'Seleccionar'}
                            </span>
                         </div>
                         <div className="flex flex-wrap gap-3">
                            {values.map(value => {
                               const isSelected = selectedAttributes[type] === value;
                               const isAvailable = availableOptions[type]?.includes(value);
                               const isDisabled = !isAvailable && Object.keys(selectedAttributes).length > 0;
                               
                               return (
                                  <button
                                     key={value}
                                     onClick={() => !isDisabled && handleAttributeSelect(type, value)}
                                     disabled={isDisabled}
                                     className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 border ${
                                        isSelected 
                                        ? 'bg-white text-black border-white shadow-[0_0_20px_rgba(255,255,255,0.3)]' 
                                        : isDisabled
                                        ? 'bg-white/5 text-white/20 border-white/5 cursor-not-allowed opacity-40'
                                        : 'bg-white/5 text-white/70 border-white/5 hover:border-white/20 hover:bg-white/10'
                                     }`}
                                  >
                                     {value}
                                  </button>
                               );
                            })}
                         </div>
                      </div>
                   ))}
                </div>
             )}

             {/* CANTIDAD Y COMPRA */}
             <div className="pt-6 border-t border-white/5 space-y-6">
                <div className="flex items-center gap-6">
                   <span className="text-sm font-bold text-white uppercase tracking-wider">Cantidad</span>
                   
                   <div className="flex items-center bg-[#0F0F0F] rounded-xl border border-white/10 p-1 w-40">
                      <button 
                         onClick={() => setQuantity(prev => Math.max(1, (Number(prev) || 0) - 1))}
                         className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/70 transition-colors shrink-0"
                      >
                         <Minus className="h-4 w-4" />
                      </button>
                      
                      <input 
                         type="number"
                         min="1"
                         max={displayStock}
                         value={quantity}
                         onChange={handleQuantityChange}
                         onBlur={handleBlur}
                         className="w-full bg-transparent border-none text-center font-mono text-white focus:ring-0 p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />

                      <button 
                         onClick={() => setQuantity(prev => Math.min(displayStock, (Number(prev) || 0) + 1))}
                         disabled={quantity >= displayStock}
                         className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/70 transition-colors disabled:opacity-30 shrink-0"
                      >
                         <Plus className="h-4 w-4" />
                      </button>
                   </div>
                   
                   <div className="text-xs text-white/40 font-mono">
                      {displayStock} Disponibles
                   </div>
                </div>

                <button
                   onClick={handleAddToCart}
                   disabled={displayStock === 0 || (hasVariants && !allAttributesSelected)}
                   className={`w-full py-5 rounded-2xl font-bold text-lg uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 ${
                      displayStock === 0
                         ? 'bg-red-500/10 text-red-500 border border-red-500/20 cursor-not-allowed'
                         : (hasVariants && !allAttributesSelected)
                         ? 'bg-white/10 text-white/40 cursor-not-allowed'
                         : 'bg-white text-black hover:bg-indigo-50 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]'
                   }`}
                >
                   <ShoppingBag className="h-5 w-5" />
                   {displayStock === 0 ? 'Agotado' : 'Agregar al Carrito'}
                </button>

                {/* Botón de WhatsApp */}
                {storeInfo?.settings?.whatsapp_enabled && storeInfo?.settings?.whatsapp_number && (
                  <a 
                    href={`https://wa.me/${storeInfo.settings.whatsapp_number.replace(/[^0-9]/g, '')}?text=Hola, me interesa este producto: ${producto?.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-4 rounded-2xl font-bold text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 hover:scale-[1.02] mt-3"
                  >
                    <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold">W</span>
                    </div>
                    Consultar por WhatsApp
                  </a>
                )}
             </div>
          </div>
        </div>
      </main>

      {/* Modal de Zoom */}
      {isImageModalOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-xl" onClick={() => setIsImageModalOpen(false)}>
          <button onClick={() => setIsImageModalOpen(false)} className="absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-50">
            <X className="h-6 w-6" />
          </button>
          <div className="relative w-full h-full flex items-center justify-center">
            <img 
               src={allImages[selectedImageIndex]} 
               className="max-w-full max-h-[90vh] object-contain drop-shadow-[0_0_50px_rgba(255,255,255,0.1)]" 
               onClick={(e) => e.stopPropagation()}
               alt=""
            />
             {allImages.length > 1 && (
               <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3">
                 {allImages.map((_, idx) => (
                   <button
                     key={idx}
                     onClick={(e) => { e.stopPropagation(); setSelectedImageIndex(idx); }}
                     className={`w-2.5 h-2.5 rounded-full transition-all ${selectedImageIndex === idx ? 'bg-white scale-125' : 'bg-white/30 hover:bg-white/60'}`}
                   />
                 ))}
               </div>
             )}
          </div>
        </div>
      )}

      {/* Footer con Términos y Condiciones */}
      <footer className="mt-16 border-t border-white/5 pt-8 pb-6">
        <div className="text-center space-y-3">
          <p className="text-xs text-white/40">
            © 2026 {producto?.tienda?.nombre || 'Tienda'}. Todos los derechos reservados.
          </p>
          <div className="flex items-center justify-center gap-4 text-xs">
            <a
              href="/terminos-clientes"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-emerald-400 transition-colors underline"
            >
              Términos y Condiciones
            </a>
            <span className="text-white/20">•</span>
            <span className="text-white/30">
              Powered by <span className="text-emerald-400 font-semibold">TiendasYA</span>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}